import { useEffect, useRef, useState } from 'react';

/**
 * スクロールアニメーション用カスタムフック
 * 要素がビューポートに入ったときに一度だけアニメーションを発火
 *
 * @param {Object} options - IntersectionObserver のオプション
 * @param {number} options.threshold - 要素がどのくらい表示されたらトリガーするか (0-1)
 * @param {string} options.rootMargin - ルートマージン (例: '-100px 0px')
 * @returns {[React.RefObject, boolean]} - [ref, isVisible]
 */
export const useScrollAnimation = (options = {}) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [hasAnimated, setHasAnimated] = useState(false);

  const { threshold = 0.1, rootMargin = '0px' } = options;

  useEffect(() => {
    const currentRef = ref.current;

    // 既にアニメーションが発火している場合は何もしない
    if (hasAnimated || !currentRef) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated) {
          setIsVisible(true);
          setHasAnimated(true); // 一度だけ発火
        }
      },
      {
        threshold,
        rootMargin,
      }
    );

    observer.observe(currentRef);

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [hasAnimated, threshold, rootMargin]);

  return [ref, isVisible];
};

/**
 * 複数の子要素に対して順次アニメーションを適用するフック
 *
 * @param {number} itemCount - アニメーション対象の要素数
 * @param {number} delayIncrement - 各要素間の遅延（ミリ秒）
 * @returns {[React.RefObject, boolean[]]} - [ref, visibilityStates]
 */
export const useStaggerAnimation = (itemCount, delayIncrement = 100) => {
  const ref = useRef(null);
  const [triggered, setTriggered] = useState(false);
  const [visibleItems, setVisibleItems] = useState(
    Array(itemCount).fill(false)
  );

  useEffect(() => {
    const currentRef = ref.current;
    if (!currentRef || triggered) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !triggered) {
          setTriggered(true);

          // 順次アニメーションを発火
          for (let i = 0; i < itemCount; i++) {
            setTimeout(() => {
              setVisibleItems(prev => {
                const newState = [...prev];
                newState[i] = true;
                return newState;
              });
            }, i * delayIncrement);
          }
        }
      },
      {
        threshold: 0.1,
        rootMargin: '0px',
      }
    );

    observer.observe(currentRef);

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [itemCount, delayIncrement, triggered]);

  return [ref, visibleItems];
};

export default useScrollAnimation;
